import { useMemo, useState } from 'react';
import { ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useImportEndpointTests } from '@/hooks/useEndpointTests';
import { useToast } from '@/contexts/ToastContext';
import { useFlag } from '@/hooks/useFeatureFlags';
import { parseSchemaProperties } from '@/utils/schemaUtils';
import {
  buildImportPreview,
  buildImportRequest,
  ImportFileError,
  parseImportFile,
  prefillColumnMap,
  type ParsedImportFile,
} from './importMapping';
import type { Endpoint } from '@/types';
import type { ImportEndpointTestsResult } from '@/types/endpointTests';

interface ImportTestsDialogProps {
  endpoint: Endpoint;
  onClose: () => void;
}

type Step = 'file' | 'map' | 'preview' | 'done';

const PREVIEW_ROWS = 5;

/**
 * Import tests from a CSV or JSON file (task 1175), in four steps: drop the
 * file → map its columns to the service's input fields (prefilled on exact
 * name match) → preview the first five mapped rows with created/skipped
 * counts → import. Parsing happens here in the browser; the service receives
 * the rows and the map and creates each test with `source: 'imported'`.
 */
export function ImportTestsDialog(props: ImportTestsDialogProps) {
  // Import answers to the test runs lever (task 1215); its trigger is gated too.
  const runsEnabled = useFlag('innovate.fe.testing.runs');
  return runsEnabled ? <ImportTestsDialogContent {...props} /> : null;
}

function ImportTestsDialogContent({ endpoint, onClose }: ImportTestsDialogProps) {
  const [step, setStep] = useState<Step>('file');
  const [parsed, setParsed] = useState<ParsedImportFile | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [columnMap, setColumnMap] = useState<Record<string, string>>({});
  const [result, setResult] = useState<ImportEndpointTestsResult | null>(null);
  const importTests = useImportEndpointTests();
  const toast = useToast();

  const fields = useMemo(
    () => parseSchemaProperties(endpoint.input as Record<string, unknown> | null),
    [endpoint.input],
  );
  const preview = useMemo(
    () => (parsed ? buildImportPreview(parsed, columnMap) : null),
    [parsed, columnMap],
  );

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setFileError(null);
    try {
      const next = await parseImportFile(file);
      setParsed(next);
      setColumnMap(prefillColumnMap(next.columns, fields.map((field) => field.name)));
    } catch (err) {
      setParsed(null);
      setFileError(err instanceof ImportFileError ? err.message : 'This file could not be read.');
    }
  };

  const setTarget = (column: string, target: string) => {
    setColumnMap((prev) => {
      const next = { ...prev };
      if (target) next[column] = target;
      else delete next[column];
      return next;
    });
  };

  const handleImport = async () => {
    if (!parsed) return;
    try {
      const request = buildImportRequest(parsed, columnMap);
      const answer = await importTests.mutateAsync({ endpointId: endpoint.id, ...request });
      setResult(answer);
      setStep('done');
      toast.success('Scenarios imported', `${answer.created} created`);
    } catch (err) {
      // A refused map names the column in `validationErrors` (shipped contract).
      const detail = (err as { validationErrors?: Array<{ field: string; message: string }> }).validationErrors?.[0];
      toast.error(
        'Import failed',
        detail ? `${detail.field}: ${detail.message}` : err instanceof Error ? err.message : undefined,
      );
    }
  };

  const sampleFor = (column: string) => {
    const row = parsed?.rows.find((candidate) => candidate[column] !== undefined && candidate[column] !== '');
    const value = row?.[column];
    if (value === undefined) return '';
    const text = typeof value === 'string' ? value : JSON.stringify(value);
    return text.length > 40 ? `${text.slice(0, 40)}…` : text;
  };

  const selectClass =
    'rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-xs text-gray-900 dark:text-white';
  const secondaryButton =
    'px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50';
  const primaryButton =
    'px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-tests-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <h3 id="import-tests-title" className="text-lg font-medium text-gray-900 dark:text-white">
            Import scenarios
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {step === 'file' && (
          <div className="space-y-4" data-testid="import-step-file">
            <label
              htmlFor="import-tests-file"
              className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 px-4 py-8 text-sm text-gray-600 dark:text-gray-300 cursor-pointer hover:border-indigo-400"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                void handleFile(e.dataTransfer.files?.[0]);
              }}
            >
              <ArrowUpTrayIcon className="h-6 w-6 text-gray-400" />
              <span>Drop a file here, or choose a CSV or JSON file</span>
              <span className="text-xs text-gray-400">
                Columns can map to input fields, plus optional name, description and expected columns.
              </span>
            </label>
            <input
              id="import-tests-file"
              type="file"
              accept=".csv,.json,text/csv,application/json"
              aria-label="Choose a CSV or JSON file"
              className="sr-only"
              onChange={(e) => void handleFile(e.target.files?.[0])}
            />
            {fileError && <p className="text-xs text-red-500">{fileError}</p>}
            {parsed && (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">{parsed.fileName}</span>
                <span className="text-gray-500 dark:text-gray-400">
                  {' '}· {parsed.rows.length} rows · {parsed.columns.length} columns
                </span>
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button type="button" onClick={onClose} className={secondaryButton}>Cancel</button>
              <button
                type="button"
                onClick={() => setStep('map')}
                disabled={!parsed || parsed.rows.length === 0}
                className={primaryButton}
              >
                Next: map columns
              </button>
            </div>
          </div>
        )}

        {step === 'map' && parsed && (
          <div className="space-y-4" data-testid="import-step-map">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Map each column of <span className="font-medium">{parsed.fileName}</span> to an input
              field. Columns whose names match are already mapped.
            </p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                  <th className="py-1 pr-2 font-medium">File column</th>
                  <th className="py-1 pr-2 font-medium">Maps to</th>
                  <th className="py-1 font-medium">Sample</th>
                </tr>
              </thead>
              <tbody>
                {parsed.columns.map((column) => (
                  <tr key={column} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="py-1.5 pr-2 font-mono text-xs text-gray-800 dark:text-gray-200">{column}</td>
                    <td className="py-1.5 pr-2">
                      <select
                        aria-label={`Map column ${column}`}
                        value={columnMap[column] ?? ''}
                        onChange={(e) => setTarget(column, e.target.value)}
                        className={selectClass}
                      >
                        <option value="">— skip —</option>
                        <optgroup label="Input fields">
                          {fields.map((field) => (
                            <option key={field.name} value={field.name}>
                              {field.name}{field.required ? ' (required)' : ''}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Scenario">
                          <option value="name">name</option>
                          <option value="description">description</option>
                          <option value="expected">expected</option>
                        </optgroup>
                      </select>
                    </td>
                    <td className="py-1.5 text-xs text-gray-500 dark:text-gray-400 truncate max-w-[12rem]">
                      {sampleFor(column)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {fields.length === 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                This AI Service has no input fields defined, so every row would be skipped.
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setStep('file')} className={secondaryButton}>Back</button>
              <button type="button" onClick={() => setStep('preview')} className={primaryButton}>
                Next: preview
              </button>
            </div>
          </div>
        )}

        {step === 'preview' && parsed && preview && (
          <div className="space-y-4" data-testid="import-step-preview">
            <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="import-counts">
              {preview.created} will be created · {preview.skipped} skipped
            </p>
            {preview.skipped > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Rows with no value in a mapped input field are skipped.
              </p>
            )}
            <ul className="space-y-2">
              {preview.rows.slice(0, PREVIEW_ROWS).map((row) => (
                <li
                  key={row.index}
                  className="rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2 text-xs"
                >
                  <p className="font-medium text-gray-900 dark:text-white">
                    {row.name || `Row ${row.index + 1}`}
                  </p>
                  <p className="text-gray-500 dark:text-gray-400">{row.description}</p>
                  <pre className="mt-1 font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                    {JSON.stringify(row.input)}
                  </pre>
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setStep('map')} className={secondaryButton}>Back</button>
              <button
                type="button"
                onClick={handleImport}
                disabled={preview.created === 0 || importTests.isPending}
                className={primaryButton}
              >
                {importTests.isPending ? 'Importing…' : `Import ${preview.created} scenarios`}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && result && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="import-result">
              {result.created} created · {result.skipped} skipped
            </p>
            <div className="flex justify-end">
              <button type="button" onClick={onClose} className={primaryButton}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImportTestsDialog;
